import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, X, RotateCcw, Send, Loader2, Bot, ArrowRight } from 'lucide-react'
import { authFetch, authFetchJson } from '../lib/api'

// Puente a la acción: cada tipo de acción del copiloto → ruta de la app (con prefill).
const ACTION_ROUTE = {
  create_post:     (p) => `/studio/create?objective=${encodeURIComponent(p?.objective || 'result')}&message=${encodeURIComponent(p?.message || '')}`,
  create_campaign: () => '/campaigns/new',
  create_landing:  () => '/landings/new',
  create_quiz:     () => '/quizzes',
  connect_meta:    () => '/connections',
  configure_brand: () => '/studio/setup',
  view_leads:      (p) => p?.filter === 'new' ? '/crm?stage=nuevo' : '/crm',
}

// ---------------------------------------------------------------------------
// Burbuja de mensaje individual
// ---------------------------------------------------------------------------
function MessageBubble({ role, content, actions, onAction }) {
  const isUser = role === 'user'
  const validActions = (!isUser && Array.isArray(actions)) ? actions.filter(a => a && ACTION_ROUTE[a.type] && a.label) : []
  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
        {!isUser && (
          <div
            className="w-7 h-7 rounded-full flex-shrink-0 mr-2 mt-0.5 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #22D3EE, #8B5CF6)' }}
          >
            <Bot size={13} className="text-white" />
          </div>
        )}
        <div
          className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isUser
              ? 'rounded-tr-sm text-white border border-aurora-violet/30'
              : 'rounded-tl-sm bg-surface-2 border border-line text-white/90'
          }`}
          style={isUser ? { background: 'rgba(139,92,246,0.18)' } : undefined}
        >
          {content}
        </div>
      </div>
      {validActions.length > 0 && (
        <div className="flex flex-col gap-1.5 mt-2 ml-9 w-[82%]">
          {validActions.map((a, i) => (
            <button
              key={i}
              onClick={() => onAction(a)}
              className="flex items-center justify-between gap-2 text-xs font-semibold px-3 py-2 rounded-xl aurora-gradient text-white hover:opacity-90 transition-all text-left"
            >
              <span className="truncate">{a.label}</span>
              <ArrowRight size={13} className="flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Spinner de "pensando"
// ---------------------------------------------------------------------------
function ThinkingBubble() {
  return (
    <div className="flex justify-start items-end gap-2">
      <div
        className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #22D3EE, #8B5CF6)' }}
      >
        <Bot size={13} className="text-white" />
      </div>
      <div className="bg-surface-2 border border-line px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-aurora-cyan/70 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-aurora-violet/70 animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-aurora-cyan/70 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Widget principal
// ---------------------------------------------------------------------------
export default function CopilotWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)   // cargando historial inicial
  const [sending, setSending] = useState(false)   // enviando mensaje
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [suggestions, setSuggestions] = useState([])

  const navigate = useNavigate()
  const runAction = useCallback((action, suggestionId) => {
    const build = ACTION_ROUTE[action?.type]
    if (!build) return
    // Quitado optimista: eliminar el chip al instante antes de navegar.
    if (suggestionId != null) {
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId))
    }
    setOpen(false)
    navigate(build(action.params || {}))
  }, [navigate])

  const dismissSuggestion = useCallback(async (e, s) => {
    e.stopPropagation()
    // Quitado optimista inmediato.
    setSuggestions(prev => prev.filter(x => x.id !== s.id))
    // Persistir en backend (fire-and-forget).
    if (s.sig) {
      authFetch('/api/copilot/suggestions/dismiss', {
        method: 'POST',
        body: { sig: s.sig },
      }).catch(() => {})
    }
  }, [])

  const bottomRef = useRef(null)
  const textareaRef = useRef(null)

  // ------------------------------------------------------------------
  // Auto-scroll al último mensaje
  // ------------------------------------------------------------------
  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, sending, open])

  // ------------------------------------------------------------------
  // Cargar historial (solo la primera vez que se abre)
  // ------------------------------------------------------------------
  const loadHistory = useCallback(async () => {
    setLoading(true)
    try {
      const data = await authFetchJson('/api/copilot/history')
      setMessages(data.messages || [])
    } catch {
      setMessages([
        {
          role: 'assistant',
          content: 'Hola, soy tu Copiloto de marketing de ClixyAds. Estoy aquí para ayudarte a crecer. ¿Por dónde empezamos?',
        },
      ])
    } finally {
      setLoading(false)
      setHistoryLoaded(true)
    }
  }, [])

  // ------------------------------------------------------------------
  // Recargar sugerencias cada vez que se abre el widget
  // ------------------------------------------------------------------
  const loadSuggestions = useCallback(async () => {
    try {
      const d = await authFetchJson('/api/copilot/suggestions')
      setSuggestions(d.suggestions || [])
    } catch { /* silencioso */ }
  }, [])

  useEffect(() => {
    if (open && !historyLoaded) {
      loadHistory()
    }
  }, [open, historyLoaded, loadHistory])

  // Sugerencias se re-piden en cada apertura (independiente del historial).
  useEffect(() => {
    if (open) {
      loadSuggestions()
    }
  }, [open, loadSuggestions])

  // ------------------------------------------------------------------
  // Foco en textarea al abrir
  // ------------------------------------------------------------------
  useEffect(() => {
    if (open && !loading) {
      setTimeout(() => textareaRef.current?.focus(), 100)
    }
  }, [open, loading])

  // ------------------------------------------------------------------
  // Enviar mensaje
  // ------------------------------------------------------------------
  const sendMessage = useCallback(async () => {
    const text = input.trim()
    if (!text || sending) return

    const userMsg = { role: 'user', content: text }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setSending(true)

    try {
      const data = await authFetchJson('/api/copilot/chat', {
        method: 'POST',
        body: { message: text },
      })
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply, actions: data.actions }])
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Ups, algo falló. Inténtalo de nuevo.' },
      ])
    } finally {
      setSending(false)
      setTimeout(() => textareaRef.current?.focus(), 50)
    }
  }, [input, sending])

  // ------------------------------------------------------------------
  // Reiniciar conversación
  // ------------------------------------------------------------------
  const resetHistory = useCallback(async () => {
    if (!window.confirm('¿Reiniciar la conversación con el Copiloto?')) return
    try {
      await authFetchJson('/api/copilot/history', { method: 'DELETE' })
    } catch { /* ignorar */ }
    setMessages([])
    setHistoryLoaded(false)
    await loadHistory()
  }, [loadHistory])

  // ------------------------------------------------------------------
  // Manejador de teclado en textarea
  // ------------------------------------------------------------------
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        sendMessage()
      }
    },
    [sendMessage]
  )

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <>
      {/* ── Botón flotante ─────────────────────────────────────── */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-label="Copiloto de marketing"
        title="Copiloto de marketing"
        className="fixed bottom-20 right-4 md:bottom-5 md:right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform duration-200 hover:scale-110 active:scale-95 focus:outline-none focus:ring-4 focus:ring-aurora-violet/40"
        style={{
          background: 'linear-gradient(135deg, #22D3EE 0%, #8B5CF6 55%, #EC4899 100%)',
          boxShadow: '0 8px 32px rgba(139,92,246,0.45), 0 2px 8px rgba(0,0,0,0.5)',
        }}
      >
        <Sparkles size={24} className="text-white drop-shadow" />

        {/* Pulso para invitar a abrirlo */}
        {!open && (
          <span
            className="absolute inset-0 rounded-full opacity-60 animate-ping"
            style={{
              background: 'linear-gradient(135deg, #22D3EE, #8B5CF6)',
              animationDuration: '2.5s',
            }}
          />
        )}
      </button>

      {/* ── Panel de chat ──────────────────────────────────────── */}
      {open && (
        <div
          className="fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-line shadow-2xl"
          style={{
            // Móvil: casi toda la pantalla
            // Desktop: esquina inferior derecha
            bottom: 'clamp(0.75rem, env(safe-area-inset-bottom, 0.75rem), 1.25rem)',
            right: 'clamp(0.75rem, 1.25rem, 1.25rem)',
            left: 'clamp(0.75rem, auto, auto)',
            top: 'clamp(4rem, auto, auto)',
            width: 'clamp(0px, calc(100vw - 1.5rem), 380px)',
            maxHeight: 'calc(100dvh - 5rem)',
            minHeight: '400px',
            background: 'var(--color-surface, #0d0d14)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 4px 16px rgba(139,92,246,0.2)',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-line flex-shrink-0">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #22D3EE, #8B5CF6)' }}
            >
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold leading-tight">Copiloto</p>
              <p className="text-dim text-xs">Tu estratega de marketing</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetHistory}
                title="Reiniciar conversación"
                aria-label="Reiniciar conversación"
                className="p-2 rounded-xl text-dim hover:text-white hover:bg-surface-2 transition-all"
              >
                <RotateCcw size={15} />
              </button>
              <button
                onClick={() => setOpen(false)}
                title="Cerrar"
                aria-label="Cerrar Copiloto"
                className="p-2 rounded-xl text-dim hover:text-white hover:bg-surface-2 transition-all"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Área de mensajes */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-full py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 size={28} className="text-aurora-violet-soft animate-spin" />
                  <p className="text-dim text-sm">Cargando historial…</p>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <MessageBubble key={i} role={msg.role} content={msg.content} actions={msg.actions} onAction={runAction} />
                ))}
                {!sending && suggestions.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    <p className="text-dim text-[11px] px-1 uppercase tracking-wide">Sugerencias para ti</p>
                    {suggestions.map(s => (
                      <div
                        key={s.id}
                        className="w-full flex items-center gap-1 text-left text-xs bg-surface-2 border border-line rounded-xl hover:border-aurora-violet/40 transition-all"
                      >
                        <button
                          onClick={() => runAction(s.action, s.id)}
                          className="flex-1 flex items-center gap-2 px-3 py-2.5 min-w-0"
                        >
                          <span className="flex-1 text-white/85 leading-snug text-left">{s.text}</span>
                          <ArrowRight size={13} className="text-aurora-violet-soft flex-shrink-0" />
                        </button>
                        <button
                          onClick={(e) => dismissSuggestion(e, s)}
                          title="Descartar sugerencia"
                          aria-label="Descartar sugerencia"
                          className="p-2 mr-1 rounded-lg text-dim hover:text-white hover:bg-white/5 transition-all flex-shrink-0"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {sending && <ThinkingBubble />}
              </>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t border-line p-3 flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending || loading}
                placeholder="Escribe tu pregunta… (Enter para enviar)"
                rows={1}
                className="flex-1 resize-none bg-surface-2 border border-line rounded-xl px-3.5 py-2.5 text-sm text-white placeholder:text-dim focus:outline-none focus:border-aurora-violet/50 focus:ring-1 focus:ring-aurora-violet/30 transition-all disabled:opacity-50 leading-relaxed"
                style={{
                  minHeight: '42px',
                  maxHeight: '96px',
                  overflowY: input.split('\n').length > 3 ? 'auto' : 'hidden',
                }}
                onInput={e => {
                  // auto-resize
                  e.target.style.height = 'auto'
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || sending || loading}
                aria-label="Enviar mensaje"
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, #22D3EE 0%, #8B5CF6 60%, #EC4899 100%)',
                  boxShadow: '0 4px 12px rgba(139,92,246,0.4)',
                }}
              >
                {sending ? (
                  <Loader2 size={16} className="text-white animate-spin" />
                ) : (
                  <Send size={16} className="text-white" />
                )}
              </button>
            </div>
            <p className="text-dim text-[10px] mt-2 text-center tracking-wide">
              Shift + Enter para salto de línea · ClixyAds Copiloto
            </p>
          </div>
        </div>
      )}
    </>
  )
}
